import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarCheck2,
  MapPin,
  MessageSquare,
  Search,
  Sparkles,
  Users,
} from 'lucide-react';
import { Button } from '@tark/shared/components';
import { useAuth } from '@tark/shared/hooks';
import BrandMark from '../components/BrandMark';
import heroBuzzScreenshot from '../assets/marketing/hero-buzz.jpg';
import heroPlayScreenshot from '../assets/marketing/hero-play.jpg';
import heroSquadsScreenshot from '../assets/marketing/hero-squads.webp';

const pillars = [
  {
    title: 'Squads',
    description: 'Group chats, weekly coordination, and the local rhythm that keeps everyone showing up.',
    icon: Users,
  },
  {
    title: 'Sessions',
    description: 'Schedules, RSVPs, scorekeeping, and the official details that turn tennis nights into real structure.',
    icon: CalendarCheck2,
  },
  {
    title: 'Buzz',
    description: 'A sports feed for local moments, lessons, rankings, and what is happening around you.',
    icon: Sparkles,
  },
];

const providerHighlights = [
  {
    title: 'Browse verified PROs',
    description: 'Find coaches, academies, and stringers near you without leaving TARK.',
    icon: Search,
  },
  {
    title: 'Book and message in one place',
    description: 'Keep the conversation moving from discovery into the real scheduling details.',
    icon: MessageSquare,
  },
  {
    title: 'Free while you are in beta',
    description: 'The provider path is open now while we keep polishing the product with real usage.',
    icon: Sparkles,
  },
];

const heroScreenSlots = {
  left: {
    cardClassName: 'w-[156px] sm:w-[176px] lg:w-[196px]',
    slotClassName: 'left-[1%] top-16 z-30 sm:left-[3%] sm:top-20 lg:left-0 lg:top-28',
  },
  center: {
    cardClassName: 'w-[184px] sm:w-[208px] lg:w-[228px]',
    slotClassName: 'left-1/2 top-3 z-40 -translate-x-1/2 sm:top-4 lg:top-5',
  },
  right: {
    cardClassName: 'w-[156px] sm:w-[176px] lg:w-[196px]',
    slotClassName: 'right-[1%] top-20 z-20 sm:right-[3%] sm:top-24 lg:right-0 lg:top-32',
  },
} as const;

type HeroScreenSlot = keyof typeof heroScreenSlots;

interface HeroScreen {
  slot: HeroScreenSlot;
  label: string;
  imageUrl: string;
  alt: string;
}

const fallbackHeroScreens: HeroScreen[] = [
  {
    slot: 'left',
    label: 'Squads',
    imageUrl: heroSquadsScreenshot,
    alt: 'TARK squads screen',
  },
  {
    slot: 'center',
    label: 'Play',
    imageUrl: heroPlayScreenshot,
    alt: 'TARK play screen',
  },
  {
    slot: 'right',
    label: 'Buzz',
    imageUrl: heroBuzzScreenshot,
    alt: 'TARK buzz screen',
  },
] as const;

function isValidHeroScreen(value: unknown): value is HeroScreen {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<HeroScreen>;

  return (
    (candidate.slot === 'left' || candidate.slot === 'center' || candidate.slot === 'right') &&
    typeof candidate.label === 'string' &&
    typeof candidate.imageUrl === 'string' &&
    typeof candidate.alt === 'string'
  );
}

function isValidHeroManifest(value: unknown): value is { phones: HeroScreen[] } {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as { phones?: unknown };

  return Array.isArray(candidate.phones) && candidate.phones.length === 3 && candidate.phones.every(isValidHeroScreen);
}

function ProductPreview() {
  const [heroScreens, setHeroScreens] = useState<HeroScreen[]>(fallbackHeroScreens);

  useEffect(() => {
    const controller = new AbortController();

    async function loadHeroManifest() {
      try {
        const response = await fetch('/content/homepage-hero.json', {
          cache: 'no-store',
          signal: controller.signal,
        });

        if (!response.ok) {
          return;
        }

        const manifest = await response.json();

        if (!isValidHeroManifest(manifest)) {
          return;
        }

        const orderedScreens = [...manifest.phones].sort((a, b) => {
          const order = { left: 0, center: 1, right: 2 } as const;
          return order[a.slot] - order[b.slot];
        });

        setHeroScreens(orderedScreens);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.warn('Could not load homepage hero manifest, using bundled defaults.', error);
        }
      }
    }

    void loadHeroManifest();

    return () => controller.abort();
  }, []);

  return (
    <div className="mx-auto max-w-[620px]">
      <div className="hero-phone-stack relative h-[420px] sm:h-[470px] lg:h-[560px]">
        <div className="absolute left-1/2 top-10 h-28 w-28 -translate-x-1/2 rounded-full bg-primary/12 blur-3xl" />
        <div className="absolute left-6 top-28 h-20 w-20 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute right-8 top-24 h-24 w-24 rounded-full bg-primary/10 blur-3xl" />

        {heroScreens.map((screen) => (
          <div key={screen.label} className={`hero-phone-slot absolute ${heroScreenSlots[screen.slot].cardClassName} ${heroScreenSlots[screen.slot].slotClassName}`}>
            <div
              className="hero-phone-card relative aspect-[220/456] overflow-hidden rounded-[30px] border border-white/70 bg-[#0a1712] p-2 shadow-[0_30px_60px_rgba(13,27,20,0.24)]"
              tabIndex={0}
              role="img"
              aria-label={screen.alt}
            >
              <img
                src={screen.imageUrl}
                alt=""
                aria-hidden="true"
                className="h-full w-full rounded-[24px] object-cover object-top"
              />
              <div className="hero-phone-label pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-white/70 bg-white/92 px-3 py-1 text-[11px] font-semibold tracking-[0.04em] text-text shadow-sm backdrop-blur">
                {screen.label}
              </div>
            </div>
          </div>
        ))}

        <div className="absolute inset-x-12 bottom-0 h-14 rounded-full bg-[radial-gradient(circle,rgba(8,135,90,0.14),transparent_72%)] blur-2xl" />
      </div>
    </div>
  );
}

export default function HomePage() {
  const { isAuthenticated, logout, isLoggingOut } = useAuth();

  const ctas = useMemo(() => {
    if (isAuthenticated) {
      return {
        primaryHref: '/welcome',
        primaryLabel: 'Open App',
        proHref: '/pros/become',
      };
    }

    return {
      primaryHref: '/register',
      primaryLabel: 'Create Account',
      proHref: '/register?intent=pro&return=%2Fpros%2Fbecome',
    };
  }, [isAuthenticated]);

  return (
    <div className="public-theme min-h-screen bg-background text-text">
      <header className="sticky top-0 z-40 border-b border-border/80 bg-background/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="shrink-0">
            <BrandMark size="sm" titleClassName="text-xl" />
          </Link>

          <nav className="hidden items-center gap-8 text-sm text-text-muted lg:flex">
            <a href="#pillars" className="transition-colors hover:text-text">Product</a>
            <a href="#providers" className="transition-colors hover:text-text">PROs</a>
            <a href="#cta" className="transition-colors hover:text-text">Get Started</a>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated ? (
              <>
                <Link to="/welcome" className="hidden sm:block">
                  <Button variant="outline" size="lg" className="rounded-full px-5">
                    Open App
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="lg"
                  className="rounded-full px-4"
                  onClick={() => logout()}
                  isLoading={isLoggingOut}
                >
                  Log Out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block">
                  <Button variant="ghost" size="lg" className="rounded-full px-4">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="lg" className="rounded-full px-6">
                    Create Account
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-border/80 bg-surface">
          <div className="absolute inset-x-0 top-0 h-full bg-[radial-gradient(circle_at_top_left,rgba(0,135,90,0.16),transparent_32%),radial-gradient(circle_at_top_right,rgba(16,185,129,0.09),transparent_26%),linear-gradient(180deg,rgba(255,255,255,0.96),rgba(244,251,247,0.9))]" />

          <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:pb-24 lg:pt-20">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">
                <MapPin className="h-4 w-4" />
                Built for local tennis and pickleball
              </div>

              <h1 className="mt-8 max-w-3xl text-text">Tennis and pickleball, in one place.</h1>

              <p className="mt-6 max-w-xl text-lg text-text-muted">
                Squads, sessions, scores, and the local sports feed, built for players, coaches,
                and clubs who want less friction and more time on court.
              </p>

              <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
                <Link to={ctas.primaryHref}>
                  <Button size="xl" className="w-full rounded-full px-7 sm:w-auto">
                    {ctas.primaryLabel}
                  </Button>
                </Link>

                <Link
                  to={ctas.proHref}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-text-muted underline decoration-border-hover underline-offset-4 transition-colors hover:text-text"
                >
                  Are you a coach or academy?
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:pl-4">
              <ProductPreview />
            </div>
          </div>
        </section>

        <section id="pillars" className="bg-background">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">The core loop</p>
              <h2 className="mt-4 text-text">Three things, done well.</h2>
              <p className="mt-5 max-w-xl text-lg text-text-muted">
                TARK works best when the front door is simple: coordinate the group, run the session,
                and stay connected to what is happening around you.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {pillars.map((pillar) => {
                const Icon = pillar.icon;

                return (
                  <article
                    key={pillar.title}
                    className="rounded-[28px] border border-border bg-surface p-6"
                  >
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 p-2.5">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="mt-6 text-text">{pillar.title}</h3>
                    <p className="mt-3 text-sm text-text-muted">{pillar.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="providers" className="border-y border-border/80 bg-surface">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="rounded-[34px] border border-border bg-background p-8 sm:p-10">
              <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr]">
                <div className="max-w-2xl">
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">PROs</p>
                  <h2 className="mt-4 text-text">Find a coach, academy, or stringer near you.</h2>
                  <p className="mt-5 text-lg text-text-muted">
                    Browse verified providers publicly, then keep the real booking, messages, and follow-up moving inside the same TARK account.
                  </p>

                  <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Link to="/pros">
                      <Button variant="outline" size="lg" className="rounded-full px-6">
                        Browse PROs
                      </Button>
                    </Link>
                    <Link to={ctas.proHref} className="text-sm font-semibold text-text-muted underline decoration-border-hover underline-offset-4 transition-colors hover:text-text">
                      Become a PRO
                    </Link>
                  </div>
                </div>

                <div className="grid gap-3">
                  {providerHighlights.map((item) => {
                    const Icon = item.icon;

                    return (
                      <div key={item.title} className="flex items-start gap-4 rounded-[24px] border border-border bg-surface px-5 py-4">
                        <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-text">{item.title}</p>
                          <p className="mt-1 text-sm text-text-muted">{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="cta" className="border-t border-border/80 bg-background">
          <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="rounded-[38px] border border-border bg-[linear-gradient(180deg,rgba(0,135,90,0.06),rgba(255,255,255,0.92))] p-8 text-center sm:p-12">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">Get started</p>
              <h2 className="mt-5 text-text">Less chasing. More playing.</h2>
              <p className="mx-auto mt-5 max-w-3xl text-lg text-text-muted">
                Create your TARK account, start with squads and sessions, and let the rest of the product open up when you need it.
              </p>

              <div className="mt-9 flex flex-col items-center gap-3">
                <Link to={ctas.primaryHref}>
                  <Button size="xl" className="rounded-full px-7">
                    {ctas.primaryLabel}
                  </Button>
                </Link>
                {!isAuthenticated ? (
                  <Link to="/login" className="text-sm font-semibold text-text-muted underline decoration-border-hover underline-offset-4 transition-colors hover:text-text">
                    Already have an account? Sign in
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.2fr_0.8fr_0.8fr_0.9fr] lg:px-8">
          <div>
            <BrandMark size="sm" titleClassName="text-xl" />
            <p className="mt-4 max-w-sm text-sm text-text-muted">
              A calmer front door for local tennis and pickleball, with private workspaces ready when the work gets bigger.
            </p>
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-text-subtle">Explore</div>
            <div className="mt-4 space-y-3 text-sm text-text-muted">
              <a href="#pillars" className="block transition-colors hover:text-text">Product</a>
              <a href="#providers" className="block transition-colors hover:text-text">PROs</a>
              <Link to="/releases" className="block transition-colors hover:text-text">What's New</Link>
              <a href="#cta" className="block transition-colors hover:text-text">Get Started</a>
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-text-subtle">Account</div>
            <div className="mt-4 space-y-3 text-sm text-text-muted">
              <Link to="/login" className="block transition-colors hover:text-text">Sign In</Link>
              <Link to="/register" className="block transition-colors hover:text-text">Create Account</Link>
              <Link to="/join-organization" className="block transition-colors hover:text-text">Join Organization</Link>
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-text-subtle">Legal</div>
            <div className="mt-4 space-y-3 text-sm text-text-muted">
              <a href="/privacy" className="block transition-colors hover:text-text">Privacy Policy</a>
              <a href="/terms" className="block transition-colors hover:text-text">Terms of Service</a>
              <a href="/data-request" className="block transition-colors hover:text-text">Data Request</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
